import React, {
	CSSProperties,
	ForwardedRef,
	forwardRef,
	memo,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { AddCircleFilled } from '@sharefiledev/icons';
import {
	colourCount,
	Component,
	ComponentTypes,
	defaultComponentDimensions,
	defaultWidth,
	getEditFlowElementById,
	isAnnotationComponent,
	SIGNER_COLORS,
} from '../../common/componentUtils';
import { Resizable } from '../../pages/Prepare/Resizable';

const BORDER_MARGIN = 4; // in pixels
const ADDICON_GAP = 2; // in pixels
const BORDER_BOX_REF = { top: 2, left: 2, right: -2, bottom: -2 };

interface Props {
	components: Component[];
	page: number;
	setComponentsToEdit: (comp: Component[]) => void;
	pageBoundingClientRect: { width: number; height: number };
	handleComponentUpdate: any;
	hideGhostComponent: any;
	componentsToEdit: Component[];
	openSignatureModal: any;
	pageHeight: number;
	areFieldsEditable: boolean;
	addComponent: (
		x: number,
		y: number,
		pageId: string,
		componentType: ComponentTypes | null,
		groupId?: string | null
	) => Component;
	resizedComponents: any;
}

export const RenderComponents = memo(
	({
		components: uItems,
		page,
		setComponentsToEdit,
		componentsToEdit,
		handleComponentUpdate,
		hideGhostComponent,
		pageBoundingClientRect,
		openSignatureModal,
		pageHeight,
		areFieldsEditable,
		addComponent,
		resizedComponents,
	}: Props) => {
		const componentsOnPage = useMemo(
			() =>
				uItems
					.filter(component => component.page === page)
					.map(component =>
						isAnnotationComponent(component.type)
							? { ...component, isAnnotation: true }
							: component
					),
			[uItems, page]
		);

		const selectedComponent = componentsToEdit?.[0];
		const signerSequence = selectedComponent?.assignedTo?.sequence ?? -1;
		const signerColor = SIGNER_COLORS[signerSequence % (colourCount - 1)];

		const scaleValue = pageBoundingClientRect.width / defaultWidth;
		const scale = scaleValue > 1 ? 1 : scaleValue;

		const isCheckboxGroupSelected = useMemo(() => {
			return selectedComponent?.type === ComponentTypes.CheckGroupComponent;
		}, [selectedComponent]);

		const checkboxGroupComponents = useMemo(() => {
			if (isCheckboxGroupSelected) {
				return componentsOnPage.filter(
					component =>
						!!component.groupId &&
						!!selectedComponent?.groupId &&
						component.groupId === selectedComponent?.groupId
				);
			}
			return [];
		}, [componentsOnPage, isCheckboxGroupSelected, selectedComponent]);

		const addCheckboxRef = useRef(null);
		const borderBoxRef = useRef(null);

		// useEffect hook for updating the border when the checkbox group is selected
		useEffect(() => {
			if (isCheckboxGroupSelected) {
				if (borderBoxRef.current !== null) {
					updateCheckboxGroupBorderBox(checkboxGroupComponents, borderBoxRef);
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [checkboxGroupComponents, isCheckboxGroupSelected, pageBoundingClientRect]);

		const updateCheckboxGroupBorderBox = useCallback(
			(checkboxGroupComps, borderBoxReference) => {
				const box = { ...BORDER_BOX_REF };

				checkboxGroupComps.forEach(component => {
					box.top = Math.min(box.top, component.y);
					box.left = Math.min(box.left, component.x);
					box.right = Math.max(box.right, component.x + component.width);
					box.bottom = Math.max(box.bottom, component.y + component.height);
				});

				const { groupTop, groupLeft, groupWidth, groupHeight } = getBorderBoxDimensions(
					box,
					pageBoundingClientRect
				);
				borderBoxReference.current.style.display = 'block';
				borderBoxReference.current.style.top = `${groupTop}px`;
				borderBoxReference.current.style.left = `${groupLeft}px`;
				borderBoxReference.current.style.width = `${groupWidth}px`;
				borderBoxReference.current.style.height = `${groupHeight}px`;
				if (addCheckboxRef.current !== null) {
					const addCheckboxRefRect: DOMRect =
						addCheckboxRef.current.getBoundingClientRect();
					// eslint-disable-next-line @typescript-eslint/no-shadow
					const scaleValue = pageBoundingClientRect.width / defaultWidth;
					const scaledAddIconGap =
						scaleValue > 1 ? ADDICON_GAP : ADDICON_GAP * scaleValue;
					if (
						pageHeight - (groupTop + groupHeight) >
						addCheckboxRefRect.height + scaledAddIconGap
					) {
						addCheckboxRef.current.style.top = `${
							groupTop + groupHeight + scaledAddIconGap
						}px`;
						addCheckboxRef.current.style.left = `${
							groupWidth / 2 - addCheckboxRefRect.width / 2 + groupLeft
						}px`;
					} else {
						if (groupHeight + addCheckboxRefRect.height * 2 > pageHeight) {
							addCheckboxRef.current.style.top = `${
								groupHeight / 2 - addCheckboxRefRect.height + groupTop
							}px`;
							addCheckboxRef.current.style.left = `${
								groupLeft + groupWidth + scaledAddIconGap
							}px`;
						} else {
							addCheckboxRef.current.style.top = `${
								groupTop - addCheckboxRefRect.height - scaledAddIconGap
							}px`;
							addCheckboxRef.current.style.left = `${
								groupWidth / 2 - addCheckboxRefRect.width / 2 + groupLeft
							}px`;
						}
					}
				}
			},
			[pageBoundingClientRect, pageHeight]
		);
		function getBorderBoxDimensions(selectedCheckBoxGroupBorder, pageBoundingBox) {
			return {
				groupTop:
					selectedCheckBoxGroupBorder.top * pageBoundingBox.height - BORDER_MARGIN,
				groupLeft:
					selectedCheckBoxGroupBorder.left * pageBoundingBox.width - BORDER_MARGIN,
				groupWidth:
					(selectedCheckBoxGroupBorder.right - selectedCheckBoxGroupBorder.left) *
						pageBoundingBox.width +
					BORDER_MARGIN * 2,
				groupHeight:
					(selectedCheckBoxGroupBorder.bottom - selectedCheckBoxGroupBorder.top) *
						pageBoundingBox.height +
					BORDER_MARGIN * 2,
			};
		}

		const updateBoundingDimensions = useCallback(
			newComponent => {
				if (borderBoxRef.current !== null) {
					const checboxComponents = checkboxGroupComponents.map(item =>
						item.id === newComponent.id ? newComponent : item
					);
					updateCheckboxGroupBorderBox(checboxComponents, borderBoxRef);
				}
			},
			[checkboxGroupComponents, updateCheckboxGroupBorderBox]
		);

		function addCheckboxToGroup(event) {
			event.stopPropagation();
			event.preventDefault();
			if (addCheckboxRef.current) {
				const addCheckboxRefRect: DOMRect =
					addCheckboxRef.current.getBoundingClientRect();
				const resizedComponent = resizedComponents?.get(
					ComponentTypes.CheckGroupComponent
				);

				const { height: defaultComponentHeight, width: defaultComponentWidth } =
					defaultComponentDimensions[ComponentTypes.CheckGroupComponent];

				const componentHeight = resizedComponent
					? resizedComponent.height * pageBoundingClientRect.height
					: defaultComponentHeight;

				const componentWidth = resizedComponent
					? resizedComponent.width * pageBoundingClientRect.width
					: defaultComponentWidth;

				const { y: parentY, x: parentX } = getEditFlowElementById(
					`overlayer-page-${page}`
				).getBoundingClientRect();

				const leftOffset = 1; // Subtracting 1px from x to align checkbox because BoundingBorder has border of 2px
				const newX =
					addCheckboxRefRect.x - leftOffset - parentX + componentWidth <
					pageBoundingClientRect.width
						? addCheckboxRefRect.x - leftOffset
						: addCheckboxRefRect.x - leftOffset - componentWidth;

				const newY =
					addCheckboxRefRect.y - parentY + componentHeight < pageHeight
						? addCheckboxRefRect.y
						: addCheckboxRefRect.y - componentHeight + addCheckboxRefRect.height;

				const newComponent = addComponent(
					newX,
					newY,
					`overlayer-page-${page}`,
					ComponentTypes.CheckGroupComponent,
					selectedComponent?.groupId
				);
				setComponentsToEdit([newComponent]);
			}
		}

		return (
			<>
				{componentsOnPage.map(component => (
					<Resizable
						componentsToEdit={componentsToEdit}
						data-testid={`Resizable-Component-${component.id}`}
						element={component}
						handleComponentUpdate={handleComponentUpdate}
						hideGhostComponent={hideGhostComponent}
						key={component.id}
						openSignatureModal={openSignatureModal}
						pageBoundingClientRect={pageBoundingClientRect}
						setComponentsToEdit={setComponentsToEdit}
						pageHeight={pageHeight}
						scale={scale}
						areFieldsEditable={areFieldsEditable}
						updateBoundingDimensions={updateBoundingDimensions}
					/>
				))}
				{/* Show border for Checkbox group component if selected */}
				{isCheckboxGroupSelected && (
					<>
						<BoundingBorder ref={borderBoxRef} signerColor={signerColor?.color} />
						<AddCheckboxDiv
							ref={addCheckboxRef}
							addCheckboxToGroup={addCheckboxToGroup}
							signerColor={signerColor?.color}
							pageWidth={pageBoundingClientRect.width}
						/>
					</>
				)}
			</>
		);
	}
);

interface AddCheckboxDivProps {
	addCheckboxToGroup: (event: any) => void;
	signerColor: string;
	pageWidth: number;
}

const AddCheckboxDiv = forwardRef(
	(
		{ addCheckboxToGroup, signerColor, pageWidth }: AddCheckboxDivProps,
		ref: ForwardedRef<HTMLDivElement>
	) => {
		const scaleValue = pageWidth / defaultWidth;
		const scale = scaleValue > 1 ? 1 : scaleValue;
		const baseIconSize = 25;
		const iconSize = baseIconSize * scale;

		return (
			<div
				ref={ref}
				style={{ position: 'absolute', zIndex: '30', cursor: 'pointer' } as CSSProperties}
				onMouseDown={e => {
					e.preventDefault();
					e.stopPropagation();
				}}
				onMouseUp={addCheckboxToGroup}
				id="add-checkbox-to-group"
				data-testid="add-checkbox-to-group"
				role="button"
			>
				<AddCircleFilled color={signerColor} size={iconSize} />
			</div>
		);
	}
);

interface BoundingBorderProps {
	signerColor: string;
}

const BoundingBorder = forwardRef(
	({ signerColor }: BoundingBorderProps, ref: ForwardedRef<HTMLDivElement>) => {
		return (
			<div
				style={{
					display: 'none',
					position: 'absolute',
					boxSizing: 'border-box',
					zIndex: 30,
					border: `2px solid ${signerColor}`,
					borderRadius: '4px',
					pointerEvents: 'none',
				}}
				data-testid="checbox-group-border-box"
				ref={ref}
			></div>
		);
	}
);
